import { Box, Button } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classnames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { TLocalesKeys as K } from '../../../../types';
import { logChatbotContactFlowQuickAccessAvailable } from '../../../bi';
import { useIsAiExperience } from '../../../common/hooks/useIsAiExperience';
import { useIsFirstPriorityExperience } from '../../../common/hooks/useIsFirstPriorityExperience';
import { selectAttachedFiles, selectAttachedIsLoading } from '../../../common/selectors/attaches';
import {
  selectConversationId,
  selectIsConnectingAgent,
  selectIsLiveChat,
} from '../../../common/selectors/chat';
import { selectErrors } from '../../../common/selectors/errors';
import { selectFieldState } from '../../../common/selectors/field';
import { selectIsCopilotInitialized } from '../../../common/selectors/meta';
import { store } from '../../../common/store';
import { sendMessageReady } from '../../../messageChannel/postNewMessages';
import { renderModel } from '../../../util/renderModel';
import { useIsAttachmentAvailable, useIsSmallScreen, useUserActionSendMessage } from '../../hooks';
import { AttachFile } from './AttachFile';
import { AttachmentsPreview } from './AttachmentsPreview';
import { CharacterCount } from './CharacterCount';
import { DirectSupportButton } from './DirectSupportButton';
import { Input } from './Input';
import { selectIsHidden } from './selectors';
import s from './styles.scss';

const isCopilot = renderModel.isCopilot();

export const Field: FC = React.memo(() => {
  const { value, disabled } = useSelector(selectFieldState);
  const isImageLoading = useSelector(selectAttachedIsLoading);
  const isLiveChat = useSelector(selectIsLiveChat);
  const attachments = useSelector(selectAttachedFiles);
  const isHidden = useSelector(selectIsHidden);
  const errors = useSelector(selectErrors);

  const emit = useUserActionSendMessage();
  const { t } = useTranslation();
  const isAttachmentAvailable = useIsAttachmentAvailable();
  const isFirstPriority = useIsFirstPriorityExperience();
  const isAiExperience = useIsAiExperience();
  const isConnectingAgent = useSelector(selectIsConnectingAgent);
  const isCopilotInitialized = useSelector(selectIsCopilotInitialized);
  const isSmallScreen = useIsSmallScreen();
  const shouldShowDirectSupportButton =
    isFirstPriority &&
    isAiExperience &&
    !disabled &&
    !isHidden &&
    // LLM node can be shown in a process of connecting an expert, so avoiding showing the button if we're already in the process
    !isConnectingAgent &&
    !errors.length;

  const conversationId = useSelector(selectConversationId);
  const shouldLogDirectSupportButton = useRef(false);

  const isDisabled = disabled && !isLiveChat;
  const hasAttachment = attachments.length > 0;
  const isSendDisabled = isDisabled || isImageLoading || !(value || hasAttachment);

  const send = useCallback(
    (action_type: string) => {
      if (!isSendDisabled) {
        emit({
          value: value ? value.trim() : t<K>('attach_message'),
          action_type,
          attachments,
          isLiveChat,
        });
      }
    },
    [attachments, emit, isLiveChat, isSendDisabled, value, t],
  );

  const onSubmit = useCallback(() => {
    send('click');
  }, [send]);

  const classNames = useMemo(() => classnames(s.field, { [s.hidden]: isHidden }), [isHidden]);

  useEffect(() => {
    if (!shouldLogDirectSupportButton.current) {
      shouldLogDirectSupportButton.current = true;
    }
  }, [conversationId]);

  useEffect(() => {
    if (shouldShowDirectSupportButton && shouldLogDirectSupportButton.current) {
      logChatbotContactFlowQuickAccessAvailable(store.getState());
    }
  }, [shouldShowDirectSupportButton]);

  useEffect(() => {
    if (isCopilot && isCopilotInitialized) {
      sendMessageReady();
    }
  }, []);

  return (
    <Box className={classNames} position="relative" direction="vertical" dataHook="input-field">
      {shouldShowDirectSupportButton && (
        <Box position="absolute" top="-42px" left="-3px">
          <DirectSupportButton />
        </Box>
      )}
      <div className={s.backdropBlur}></div>
      <Box dataHook="user-input" direction="vertical" gap="SP2" marginBottom={isSmallScreen ? 'SP2' : 'SP4'}>
        <Box direction="vertical" gap="SP1">
          <Input onSend={send} />
        </Box>
        <Box gap="SP1" verticalAlign="middle">
          {isAttachmentAvailable && (
            <>
              <AttachFile />
              <AttachmentsPreview attachments={attachments} />
            </>
          )}
          <Box flexGrow={1} align="right" alignSelf="flex-end" verticalAlign="middle" gap="SP1">
            <CharacterCount />
            <Button disabled={isSendDisabled} size="small" dataHook="send" onClick={onSubmit}>
              {t<K>('field_btn_label')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
